import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useRef, useState } from "react";
import { Download, Eye, EyeOff, RefreshCw, Trash2, X, ArrowUpDown, Columns3 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import logo from "@/assets/logo.png";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  listConversions,
  updateConversion,
  deleteConversions,
  type AdminLead,
} from "@/lib/admin-conversions.functions";

export const Route = createFileRoute("/admin/conversions")({
  head: () => ({
    meta: [
      { title: "Conversions – Schneider Bodenbeschichtungen" },
      { name: "robots", content: "noindex,nofollow" },
      {
        name: "description",
        content:
          "Internes Dashboard für Leads und Offline-Conversions der Landingpages.",
      },
    ],
  }),
  component: AdminConversionsPage,
});

const STORAGE_KEY = "schneider-admin-conversions-key";

const STATUS_OPTIONS = [
  { value: "neu", label: "Neu" },
  { value: "kontaktiert", label: "Kontaktiert" },
  { value: "angebot", label: "Angebot" },
  { value: "gewonnen", label: "Gewonnen" },
  { value: "verloren", label: "Verloren" },
];

const TRACKING_COLUMNS: { key: keyof AdminLead; label: string }[] = [
  { key: "utm_source", label: "utm_source" },
  { key: "utm_medium", label: "utm_medium" },
  { key: "utm_campaign", label: "utm_campaign" },
  { key: "utm_content", label: "utm_content" },
  { key: "utm_term", label: "utm_term" },
  { key: "campaign_name", label: "campaign_name" },
  { key: "campaign_id", label: "campaign_id" },
  { key: "adgroup_name", label: "adgroup_name" },
  { key: "adgroup_id", label: "adgroup_id" },
  { key: "ad_id", label: "ad_id" },
  { key: "keyword", label: "keyword" },
  { key: "matchtype", label: "matchtype" },
  { key: "network", label: "network" },
  { key: "device", label: "device" },
  { key: "loc_physical_ms", label: "loc_physical_ms" },
  { key: "gclid", label: "gclid" },
  { key: "gbraid", label: "gbraid" },
  { key: "wbraid", label: "wbraid" },
  { key: "landing_url", label: "landing_url" },
  { key: "referrer", label: "referrer" },
  { key: "first_touch_channel", label: "first_touch_channel" },
  { key: "first_touch_source", label: "first_touch_source" },
  { key: "first_touch_medium", label: "first_touch_medium" },
  { key: "first_touch_campaign", label: "first_touch_campaign" },
  { key: "first_touch_landing_page", label: "first_touch_landing_page" },
  { key: "first_touch_referrer", label: "first_touch_referrer" },
  { key: "first_touch_timestamp", label: "first_touch_timestamp" },
  { key: "last_touch_channel", label: "last_touch_channel" },
  { key: "last_touch_source", label: "last_touch_source" },
  { key: "last_touch_medium", label: "last_touch_medium" },
  { key: "last_touch_campaign", label: "last_touch_campaign" },
  { key: "last_touch_landing_page", label: "last_touch_landing_page" },
  { key: "last_touch_referrer", label: "last_touch_referrer" },
  { key: "last_touch_timestamp", label: "last_touch_timestamp" },
  { key: "visitor_id", label: "visitor_id" },
  { key: "session_id", label: "session_id" },
  { key: "form_id", label: "form_id" },
  { key: "form_name", label: "form_name" },
  { key: "form_page_url", label: "form_page_url" },
  { key: "form_submitted_at", label: "form_submitted_at" },
  { key: "msclkid", label: "msclkid" },
  { key: "fbclid", label: "fbclid" },
  { key: "gad_source", label: "gad_source" },
];

const BASE_COLUMNS: { key: string; label: string }[] = [
  { key: "created_at", label: "Datum" },
  { key: "name", label: "Name" },
  { key: "firma", label: "Firma" },
  { key: "telefon", label: "Telefon" },
  { key: "email", label: "E-Mail" },
  { key: "page_id", label: "Seite" },
  { key: "betreff", label: "Betreff" },
  { key: "nachricht", label: "Nachricht" },
  { key: "status", label: "Status" },
  { key: "notizen", label: "Notizen" },
  { key: "wert", label: "Wert (EUR)" },
];

const ALL_COLUMNS: { key: string; label: string }[] = [
  ...BASE_COLUMNS,
  ...TRACKING_COLUMNS.map((c) => ({ key: c.key as string, label: c.label })),
];

const HIDDEN_COLS_KEY = "schneider-admin-conversions-hidden-cols";

const CSV_COLUMNS: (keyof AdminLead)[] = [
  "created_at", "name", "firma", "email", "telefon", "ort", "betreff", "nachricht",
  "page_id", "status", "wert", "notizen",
  "gclid", "gbraid", "wbraid", "msclkid", "fbclid", "gclsrc", "gad_source",
  "utm_source", "utm_medium", "utm_source_platform", "utm_campaign", "utm_id",
  "utm_term", "utm_content",
  "campaign_name", "campaign_id", "adgroup_name", "adgroup_id", "ad_id",
  "keyword", "matchtype", "network", "device", "device_model", "placement",
  "placement_category", "adposition", "creative", "loc_physical_ms",
  "loc_interest_ms", "target_id", "extension_id", "feed_item_id",
  "asset_group_id", "ad_type", "video_source_id",
  "landing_url", "referrer", "user_agent",
  "attribution_version", "visitor_id", "session_id",
  "form_id", "form_name", "form_page_url", "form_page_title", "form_submitted_at",
  "first_touch_channel", "first_touch_source", "first_touch_medium",
  "first_touch_campaign", "first_touch_campaign_id", "first_touch_content",
  "first_touch_term", "first_touch_source_platform", "first_touch_landing_page",
  "first_touch_referrer", "first_touch_timestamp",
  "last_touch_channel", "last_touch_source", "last_touch_medium",
  "last_touch_campaign", "last_touch_campaign_id", "last_touch_content",
  "last_touch_term", "last_touch_source_platform", "last_touch_landing_page",
  "last_touch_referrer", "last_touch_timestamp",
  "dclid", "ttclid", "li_fat_id", "twclid", "sccid", "epik", "rdt_cid",
  "gad_campaignid",
];

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString("de-DE", {
    dateStyle: "short",
    timeStyle: "short",
  });
}
function fmtEur(n: number) {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(n);
}
function toISODate(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
}
function parseISODate(s: string) {
  if (!s) return undefined;
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}

type Range = { from: string; to: string; label: string };

const MONTHS_DE = [
  "Jan", "Feb", "Mär", "Apr", "Mai", "Jun",
  "Jul", "Aug", "Sep", "Okt", "Nov", "Dez",
];

function monthRange(year: number, monthIndex: number): Range {
  const start = new Date(year, monthIndex, 1);
  const end = new Date(year, monthIndex + 1, 0);
  return {
    from: toISODate(start),
    to: toISODate(end),
    label: `${MONTHS_DE[monthIndex]} ${year}`,
  };
}
function quarterRange(year: number, quarter: 1 | 2 | 3 | 4): Range {
  const start = new Date(year, (quarter - 1) * 3, 1);
  const end = new Date(year, (quarter - 1) * 3 + 3, 0);
  return { from: toISODate(start), to: toISODate(end), label: `Q${quarter} ${year}` };
}
function lastCompletedMonth(now = new Date()): Range {
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return monthRange(d.getFullYear(), d.getMonth());
}
function prevMonthOf(r: Range): Range {
  const start = parseISODate(r.from)!;
  const prev = new Date(start.getFullYear(), start.getMonth() - 1, 1);
  return monthRange(prev.getFullYear(), prev.getMonth());
}
function sameMonthLastYear(r: Range): Range {
  const start = parseISODate(r.from)!;
  return monthRange(start.getFullYear() - 1, start.getMonth());
}
function lastCompletedQuarter(now = new Date()): Range {
  const currentQuarter = Math.floor(now.getMonth() / 3) + 1;
  const q = (currentQuarter === 1 ? 4 : currentQuarter - 1) as 1 | 2 | 3 | 4;
  const year = currentQuarter === 1 ? now.getFullYear() - 1 : now.getFullYear();
  return quarterRange(year, q);
}
function prevQuarterOf(r: Range): Range {
  const start = parseISODate(r.from)!;
  const q = (Math.floor(start.getMonth() / 3) + 1) as 1 | 2 | 3 | 4;
  const prevQ = (q === 1 ? 4 : q - 1) as 1 | 2 | 3 | 4;
  const year = q === 1 ? start.getFullYear() - 1 : start.getFullYear();
  return quarterRange(year, prevQ);
}
function sameQuarterLastYear(r: Range): Range {
  const start = parseISODate(r.from)!;
  const q = (Math.floor(start.getMonth() / 3) + 1) as 1 | 2 | 3 | 4;
  return quarterRange(start.getFullYear() - 1, q);
}

function AdminConversionsPage() {
  const list = useServerFn(listConversions);
  const update = useServerFn(updateConversion);
  const remove = useServerFn(deleteConversions);

  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<AdminLead[]>([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [compare, setCompare] = useState<{ current: Range; compare: Range } | null>(null);
  const [sortKey, setSortKey] = useState<string>("created_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [hiddenCols, setHiddenCols] = useState<Set<string>>(new Set());
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(HIDDEN_COLS_KEY);
      if (raw) setHiddenCols(new Set(JSON.parse(raw) as string[]));
    } catch {
      /* ignore */
    }
  }, []);

  const toggleCol = (key: string) => {
    setHiddenCols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      try {
        localStorage.setItem(HIDDEN_COLS_KEY, JSON.stringify([...next]));
      } catch {
        /* ignore */
      }
      return next;
    });
  };
  const showCol = (key: string) => !hiddenCols.has(key);

  // Click-and-drag horizontal/vertical panning of the lead table.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let dragging = false;
    let startX = 0;
    let startY = 0;
    let startLeft = 0;
    let startTop = 0;

    const isInteractive = (target: EventTarget | null) =>
      target instanceof Element &&
      !!target.closest("input, select, textarea, button, a, [role=\"button\"]");

    const onDown = (e: PointerEvent) => {
      if (e.button !== 0 || isInteractive(e.target)) return;
      dragging = true;
      startX = e.clientX;
      startY = e.clientY;
      startLeft = el.scrollLeft;
      startTop = el.scrollTop;
      el.setPointerCapture(e.pointerId);
      el.style.cursor = "grabbing";
      el.style.userSelect = "none";
    };
    const onMove = (e: PointerEvent) => {
      if (!dragging) return;
      el.scrollLeft = startLeft - (e.clientX - startX);
      el.scrollTop = startTop - (e.clientY - startY);
    };
    const onUp = (e: PointerEvent) => {
      if (!dragging) return;
      dragging = false;
      try {
        el.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      el.style.cursor = "grab";
      el.style.userSelect = "";
    };

    el.style.cursor = "grab";
    el.addEventListener("pointerdown", onDown);
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup", onUp);
    el.addEventListener("pointercancel", onUp);
    return () => {
      el.removeEventListener("pointerdown", onDown);
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
      el.removeEventListener("pointercancel", onUp);
    };
  }, [authed]);

  const load = async (pw: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await list({ data: { password: pw } });
      setRows(res.rows);
      setAuthed(true);
      try {
        sessionStorage.setItem(STORAGE_KEY, pw);
      } catch {
        /* ignore */
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Daten konnten nicht geladen werden.");
      setAuthed(false);
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch {
        /* ignore */
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let stored = "";
    try {
      stored = sessionStorage.getItem(STORAGE_KEY) || "";
    } catch {
      /* ignore */
    }
    if (stored) {
      setPassword(stored);
      void load(stored);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredRows = useMemo(() => {
    const fromTs = fromDate ? new Date(fromDate + "T00:00:00").getTime() : null;
    const toTs = toDate ? new Date(toDate + "T23:59:59").getTime() : null;
    return rows.filter((r) => {
      const t = new Date(r.created_at).getTime();
      if (fromTs !== null && t < fromTs) return false;
      if (toTs !== null && t > toTs) return false;
      return true;
    });
  }, [rows, fromDate, toDate]);

  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];
    const dir = sortDir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      const get = (r: AdminLead) => {
        if (sortKey === "created_at") return new Date(r.created_at).getTime();
        if (sortKey === "wert") return r.wert == null ? null : Number(r.wert);
        const v = (r as unknown as Record<string, unknown>)[sortKey];
        return v == null || v === "" ? null : String(v).toLowerCase();
      };
      const av = get(a);
      const bv = get(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
    return arr;
  }, [filteredRows, sortKey, sortDir]);

  const stats = useMemo(() => {
    const now = Date.now();
    const d7 = now - 7 * 864e5;
    const d30 = now - 30 * 864e5;
    const last7 = filteredRows.filter((r) => new Date(r.created_at).getTime() >= d7).length;
    const last30 = filteredRows.filter((r) => new Date(r.created_at).getTime() >= d30).length;
    const valueTotal = filteredRows.reduce((s, r) => s + (Number(r.wert) || 0), 0);
    const valueLast30 = filteredRows
      .filter((r) => new Date(r.created_at).getTime() >= d30)
      .reduce((s, r) => s + (Number(r.wert) || 0), 0);
    const byPage = [
      ...filteredRows.reduce((m, r) => {
        const k = r.page_id || "(unbekannt)";
        m.set(k, (m.get(k) ?? 0) + 1);
        return m;
      }, new Map<string, number>()),
    ]
      .map(([page, count]) => ({ page, count }))
      .sort((a, b) => b.count - a.count);
    return { total: filteredRows.length, last7, last30, valueTotal, valueLast30, byPage };
  }, [filteredRows]);

  const compareStats = useMemo(() => {
    if (!compare) return null;
    const inRange = (r: Range) => {
      const fromTs = new Date(r.from + "T00:00:00").getTime();
      const toTs = new Date(r.to + "T23:59:59").getTime();
      return rows.filter((row) => {
        const t = new Date(row.created_at).getTime();
        return t >= fromTs && t <= toTs;
      });
    };
    const summarize = (l: AdminLead[]) => ({
      total: l.length,
      value: l.reduce((s, r) => s + (Number(r.wert) || 0), 0),
      won: l.filter((r) => r.status === "gewonnen").length,
    });
    return { cur: summarize(inRange(compare.current)), prev: summarize(inRange(compare.compare)) };
  }, [rows, compare]);

  const patchRow = async (id: string, patch: Partial<AdminLead>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    try {
      await update({
        data: {
          password,
          id,
          status: patch.status as string | undefined,
          wert: patch.wert as number | null | undefined,
          notizen: patch.notizen as string | null | undefined,
        },
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Speichern fehlgeschlagen.");
    }
  };

  const confirmDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setDeleting(true);
    try {
      await remove({ data: { password, ids } });
      setRows((prev) => prev.filter((r) => !selectedIds.has(r.id)));
      setSelectedIds(new Set());
      setDeleteOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Löschen fehlgeschlagen.");
    } finally {
      setDeleting(false);
    }
  };

  const exportCsv = () => {
    const esc = (v: unknown) => {
      const s = v == null ? "" : String(v);
      return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [CSV_COLUMNS.join(",")];
    for (const r of sortedRows) {
      lines.push(
        CSV_COLUMNS.map((c) => esc((r as unknown as Record<string, unknown>)[c])).join(",")
      );
    }
    const blob = new Blob(["\ufeff" + lines.join("\n")], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `schneider-conversions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/40 p-6">
        <div className="w-full max-w-sm rounded-xl border border-border bg-background p-6 shadow-lg">
          <h1 className="text-lg font-semibold text-foreground">
            Conversions – Schneider Bodenbeschichtungen
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">Passwort erforderlich</p>
          <label className="mt-4 block text-sm font-medium text-foreground">
            Passwort
            <div className="relative mt-1">
              <Input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void load(password);
                }}
                autoFocus
                required
                className="pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setShowPassword((s) => !s)}
                tabIndex={-1}
                aria-label={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
                className="absolute inset-y-0 right-0 h-full w-10"
              >
                {showPassword ? <EyeOff /> : <Eye />}
              </Button>
            </div>
          </label>
          {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
          <Button
            type="button"
            onClick={() => void load(password)}
            disabled={loading}
            className="mt-4 w-full"
          >
            {loading ? "Anmelden …" : "Anmelden"}
          </Button>
        </div>

      </div>
    );
  }

  const lastM = lastCompletedMonth();
  const lastQ = lastCompletedQuarter();

  return (
    <div className="min-h-screen bg-muted/40">
      <header className="bg-primary text-primary-foreground">
        <div className="mx-auto flex max-w-[1600px] flex-wrap items-center justify-between gap-3 px-6 py-6">
          <div className="flex items-center gap-4">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-background p-2 shadow-sm">
              <img
                src={logo}
                alt="Schneider Bodenbeschichtungen"
                className="h-full w-full object-contain"
              />
            </span>
            <div>
            <p className="text-xs font-semibold uppercase tracking-widest opacity-80">
              Schneider Bodenbeschichtungen
            </p>
            <h1 className="text-2xl font-bold tracking-tight">Conversions</h1>
            <p className="text-sm opacity-90">
              Alle Formularanfragen der Landingpages inkl. Werbe-Tracking und
              Offline-Conversion-Werten
            </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => void load(password)}
              disabled={loading}
            >
              <RefreshCw /> Aktualisieren
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="secondary" size="sm">
                  <Columns3 /> Spalten
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="max-h-96 w-64 overflow-auto">
                <DropdownMenuLabel>Spalten anzeigen</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {ALL_COLUMNS.map((c) => (
                  <DropdownMenuCheckboxItem
                    key={c.key}
                    checked={showCol(c.key)}
                    onSelect={(e) => e.preventDefault()}
                    onCheckedChange={() => toggleCol(c.key)}
                  >
                    {c.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button type="button" variant="secondary" size="sm" onClick={exportCsv}>
              <Download /> CSV-Export
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1600px] px-6 py-6">
        {error && (
          <p className="mb-4 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            {error}
          </p>
        )}

        {/* Compare presets */}
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-background p-3">
          <span className="mr-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Vergleich
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setCompare({ current: lastM, compare: prevMonthOf(lastM) });
              setFromDate(lastM.from);
              setToDate(lastM.to);
            }}
          >
            Letzter Monat vs. Vormonat
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setCompare({ current: lastM, compare: sameMonthLastYear(lastM) });
              setFromDate(lastM.from);
              setToDate(lastM.to);
            }}
          >
            Letzter Monat vs. Vorjahresmonat
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setCompare({ current: lastQ, compare: prevQuarterOf(lastQ) });
              setFromDate(lastQ.from);
              setToDate(lastQ.to);
            }}
          >
            Letztes Quartal vs. Vorquartal
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setCompare({ current: lastQ, compare: sameQuarterLastYear(lastQ) });
              setFromDate(lastQ.from);
              setToDate(lastQ.to);
            }}
          >
            Letztes Quartal vs. Vorjahresquartal
          </Button>
          {compare && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setCompare(null);
                setFromDate("");
                setToDate("");
              }}
            >
              <X className="h-4 w-4" /> Vergleich zurücksetzen
            </Button>
          )}
        </div>

        {/* Date filter */}
        <div className="mb-6 flex flex-wrap items-end gap-3 rounded-lg border border-border bg-background p-4">
          <label className="text-sm text-foreground">
            <span className="mb-1 block font-medium">Von</span>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-[170px]"
            />
          </label>
          <label className="text-sm text-foreground">
            <span className="mb-1 block font-medium">Bis</span>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-[170px]"
            />
          </label>
          {(fromDate || toDate) && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setFromDate("");
                setToDate("");
                setCompare(null);
              }}
            >
              Zurücksetzen
            </Button>
          )}
          <span className="ml-auto text-xs text-muted-foreground">
            {filteredRows.length} von {rows.length} Einträgen
            {compare && (
              <span className="ml-2 rounded bg-muted px-2 py-0.5 font-medium text-foreground">
                {compare.current.label} vs. {compare.compare.label}
              </span>
            )}
          </span>
        </div>

        {compare && compareStats ? (
          <div className="mb-6 grid gap-4 sm:grid-cols-3">
            <CompareStat
              label="Leads"
              cur={compareStats.cur.total}
              prev={compareStats.prev.total}
              curLabel={compare.current.label}
              prevLabel={compare.compare.label}
            />
            <CompareStat
              label="Gewonnen"
              cur={compareStats.cur.won}
              prev={compareStats.prev.won}
              curLabel={compare.current.label}
              prevLabel={compare.compare.label}
            />
            <CompareStat
              label="Wert"
              cur={compareStats.cur.value}
              prev={compareStats.prev.value}
              curLabel={compare.current.label}
              prevLabel={compare.compare.label}
              formatter={fmtEur}
            />
          </div>
        ) : (
          <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-5">
            <Stat label="Gesamt" value={stats.total} />
            <Stat label="Letzte 7 Tage" value={stats.last7} />
            <Stat label="Letzte 30 Tage" value={stats.last30} />
            <Stat label="Wert (30 Tage)" value={fmtEur(stats.valueLast30)} />
            <Stat label="Wert gesamt" value={fmtEur(stats.valueTotal)} />
          </div>
        )}

        <section className="mb-6 rounded-lg border border-border bg-background p-4">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Nach Landingpage
          </h2>
          <ul className="divide-y divide-border text-sm">
            {stats.byPage.length === 0 && (
              <li className="py-2 text-muted-foreground">Keine Leads im Zeitraum.</li>
            )}
            {stats.byPage.map((p) => (
              <li key={p.page} className="flex items-center justify-between py-2">
                <span className="truncate font-mono text-foreground">{p.page}</span>
                <span className="font-semibold text-foreground">{p.count}</span>
              </li>
            ))}
          </ul>
        </section>

        {selectedIds.size > 0 && (
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
            <span className="font-medium text-destructive">
              {selectedIds.size} Lead{selectedIds.size === 1 ? "" : "s"} ausgewählt
            </span>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setSelectedIds(new Set())}>
                Auswahl aufheben
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => setDeleteOpen(true)}
                disabled={deleting}
              >
                <Trash2 /> Löschen
              </Button>
            </div>
          </div>
        )}

        <section className="overflow-hidden rounded-lg border border-border bg-background">
          <div ref={scrollRef} className="max-h-[75vh] overflow-auto">
            <table className="min-w-full divide-y divide-border text-sm">
              <thead className="sticky top-0 z-10 bg-muted">
                <tr>
                  <th className="w-10 px-3 py-2">
                    <input
                      type="checkbox"
                      aria-label="Alle auswählen"
                      checked={
                        sortedRows.length > 0 && sortedRows.every((r) => selectedIds.has(r.id))
                      }
                      onChange={(e) =>
                        setSelectedIds(
                          e.target.checked ? new Set(sortedRows.map((r) => r.id)) : new Set()
                        )
                      }
                      className="h-4 w-4 cursor-pointer"
                    />
                  </th>
                  {ALL_COLUMNS.filter((c) => showCol(c.key)).map((col) => (
                    <th
                      key={col.key}
                      onClick={() => {
                        if (sortKey === col.key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
                        else {
                          setSortKey(col.key);
                          setSortDir(col.key === "created_at" || col.key === "wert" ? "desc" : "asc");
                        }
                      }}
                      className="cursor-pointer whitespace-nowrap px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground"
                    >
                      <span className="inline-flex items-center gap-1">
                        {col.label}
                        <ArrowUpDown className="h-3 w-3 opacity-50" />
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {sortedRows.length === 0 && (
                  <tr>
                    <td
                      colSpan={1 + ALL_COLUMNS.filter((c) => showCol(c.key)).length}
                      className="p-6 text-center text-muted-foreground"
                    >
                      Keine Leads im gewählten Zeitraum.
                    </td>
                  </tr>
                )}
                {sortedRows.map((r) => (
                  <tr key={r.id} className="align-top hover:bg-muted/50">
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        aria-label="Lead auswählen"
                        checked={selectedIds.has(r.id)}
                        onChange={() =>
                          setSelectedIds((prev) => {
                            const next = new Set(prev);
                            if (next.has(r.id)) next.delete(r.id);
                            else next.add(r.id);
                            return next;
                          })
                        }
                        className="h-4 w-4 cursor-pointer"
                      />
                    </td>
                    {showCol("created_at") && (
                    <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                      {fmtDate(r.created_at)}
                    </td>
                    )}
                    {showCol("name") && (
                    <td className="whitespace-nowrap px-3 py-2 font-medium text-foreground">
                      {r.name}
                    </td>
                    )}
                    {showCol("firma") && (
                    <td className="whitespace-nowrap px-3 py-2">{r.firma}</td>
                    )}
                    {showCol("telefon") && (
                    <td className="whitespace-nowrap px-3 py-2">
                      {r.telefon && (
                        <a href={`tel:${r.telefon}`} className="text-primary hover:underline">
                          {r.telefon}
                        </a>
                      )}
                    </td>
                    )}
                    {showCol("email") && (
                    <td className="whitespace-nowrap px-3 py-2">
                      {r.email && (
                        <a href={`mailto:${r.email}`} className="text-primary hover:underline">
                          {r.email}
                        </a>
                      )}
                    </td>
                    )}
                    {showCol("page_id") && (
                    <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">{r.page_id}</td>
                    )}
                    {showCol("betreff") && (
                    <td className="max-w-[200px] px-3 py-2 text-xs">{r.betreff}</td>
                    )}
                    {showCol("nachricht") && (
                    <td className="max-w-[320px] px-3 py-2 text-xs">
                      <div className="max-h-24 overflow-auto whitespace-pre-wrap">
                        {r.nachricht}
                      </div>
                    </td>
                    )}
                    {showCol("status") && (
                    <td className="px-3 py-2">
                      <select
                        value={r.status ?? "neu"}
                        onChange={(e) => void patchRow(r.id, { status: e.target.value })}
                        className="rounded-md border border-border bg-background px-2 py-1 text-xs"
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    )}
                    {showCol("notizen") && (
                    <td className="px-3 py-2">
                      <input
                        defaultValue={r.notizen ?? ""}
                        onBlur={(e) => {
                          const v = e.target.value;
                          if (v !== (r.notizen ?? "")) void patchRow(r.id, { notizen: v || null });
                        }}
                        placeholder="Notiz …"
                        className="w-40 rounded-md border border-border bg-background px-2 py-1 text-xs"
                      />
                    </td>
                    )}
                    {showCol("wert") && (
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        step="1"
                        defaultValue={r.wert ?? ""}
                        onBlur={(e) => {
                          const raw = e.target.value;
                          const v = raw === "" ? null : Number(raw);
                          if (v !== (r.wert ?? null)) void patchRow(r.id, { wert: v });
                        }}
                        placeholder="0"
                        className="w-24 rounded-md border border-border bg-background px-2 py-1 text-xs"
                      />
                    </td>
                    )}
                    {TRACKING_COLUMNS.filter((c) => showCol(c.key as string)).map((c) => (
                      <td
                        key={c.key as string}
                        className="max-w-[220px] truncate px-3 py-2 font-mono text-[11px] text-muted-foreground"
                        title={String(r[c.key] ?? "")}
                      >
                        {String(r[c.key] ?? "")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {selectedIds.size} Lead{selectedIds.size === 1 ? "" : "s"} endgültig löschen?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Die ausgewählten Einträge werden dauerhaft aus der Datenbank entfernt. Das kann
              nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void confirmDelete();
              }}
              disabled={deleting}
            >
              {deleting ? "Löschen …" : "Endgültig löschen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-background p-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 text-2xl font-bold text-foreground">{value}</div>
    </div>
  );
}

function CompareStat({
  label,
  cur,
  prev,
  curLabel,
  prevLabel,
  formatter,
}: {
  label: string;
  cur: number;
  prev: number;
  curLabel: string;
  prevLabel: string;
  formatter?: (n: number) => string;
}) {
  const fmt = formatter ?? ((n: number) => String(n));
  const delta = prev === 0 ? (cur === 0 ? 0 : 100) : ((cur - prev) / prev) * 100;
  const up = delta >= 0;
  return (
    <div className="rounded-lg border border-border bg-background p-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="text-2xl font-bold text-foreground">{fmt(cur)}</span>
        <span
          className={
            up ? "text-sm font-semibold text-emerald-600" : "text-sm font-semibold text-destructive"
          }
        >
          {up ? "+" : ""}
          {delta.toFixed(0)} %
        </span>
      </div>
      <div className="mt-1 text-xs text-muted-foreground">
        {curLabel}: {fmt(cur)} · {prevLabel}: {fmt(prev)}
      </div>
    </div>
  );
}
